import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  X,
  Maximize2,
  Minimize2,
  ThumbsUp,
  ThumbsDown,
  Star,
  CirclePercent,
  Quote,
  StickyNote,
  Zap,
  Megaphone,
  User,
} from 'lucide-react'
import React, { useState, useEffect, useContext } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { getDevToolIcon } from '@/components/utils/taskCreationUtils'
import type { DevTool } from '@/components/dashboard/roadmap/DevToolSelect'
import { RoadmapContext } from '@/context/RoadmapContext'
import { useCustomFetch } from '@/hooks/useCustomFetch'
import VideoCarousel from './VideoCarousel'
import VideoCarouselExpanded from './VideoCarouselExpanded'
import { useVideoUrls } from '@/hooks/useVideoUrls'
import { useProcessedVideos } from '@/hooks/useProcessedVideos'
import Tippy from '@tippyjs/react'
import { QuoteTooltip } from '@/components/dashboard/tooltips/quoteTooltip'
import { NeedTooltip } from '@/components/dashboard/tooltips/needTooltip'
import { ProblemTooltip } from '@/components/dashboard/tooltips/problemTooltip'
import { FeedbackTooltip } from '@/components/dashboard/tooltips/feedbackTooltip'

interface OpportunityDialogProps {
  isOpen: boolean
  onClose: () => void
  opportunity: any
  number?: number
  onSendToDevTool?: (e: any) => void
  devTool?: string
}

const OpportunityDialog = ({
  isOpen,
  onClose,
  opportunity,
  number,
  onSendToDevTool,
  devTool = 'jira',
}: OpportunityDialogProps) => {
  const { meetings, toggleArchiveOpportunity, evidence } = useContext(RoadmapContext)
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0)
  const [isExpanded, setIsExpanded] = useState(false)
  const [thumbsState, setThumbsState] = useState<'up' | 'down' | null>(null as 'up' | 'down' | null)
  const [isAnimating, setIsAnimating] = useState(false)
  const [currentDevTool, setCurrentDevTool] = useState(devTool)
  const { toast } = useToast()
  const customFetch = useCustomFetch()

  // Use our new video loading hooks
  const { videoUrls, isLoading: isLoadingUrls } = useVideoUrls(opportunity.Evidence || [])

  // Process videos based on the evidence data and loaded URLs
  const processedVideos = useProcessedVideos(opportunity.Evidence || [], videoUrls, meetings)

  // Listen for devTool changes to keep the dialog in sync
  useEffect(() => {
    const handleDevToolChange = (e: CustomEvent) => {
      if (e.detail) {
        console.log(`OpportunityDialog: DevTool changed to ${e.detail}`)
        setCurrentDevTool(e.detail)
      }
    }

    window.addEventListener('devToolChange', handleDevToolChange as EventListener)

    return () => {
      window.removeEventListener('devToolChange', handleDevToolChange as EventListener)
    }
  }, [])

  // Update dialog state from props
  useEffect(() => {
    if (devTool !== currentDevTool) {
      console.log(`OpportunityDialog: Updating DevTool from props: ${devTool}`)
      setCurrentDevTool(devTool)
    }
  }, [devTool, currentDevTool])

  useEffect(() => {
    if (!isOpen) {
      setIsExpanded(false)
    }
  }, [isOpen])

  // Reset video index when opening dialog
  useEffect(() => {
    if (isOpen) {
      setSelectedVideoIndex(0)
    }
  }, [isOpen])

  const handleThumbClick = (type: 'up' | 'down') => {
    setIsAnimating(true)

    // Reset animation after it completes
    setTimeout(() => {
      setIsAnimating(false)
    }, 500)

    setThumbsState((prevState) => {
      if (prevState === type) {
        // If already selected, unselect and show toast
        toast({
          title: 'Feedback removed',
          description: 'Your feedback has been removed',
        })
        return null
      }

      // Show toast based on feedback type
      toast({
        title: type === 'up' ? 'Positive feedback recorded' : 'Negative feedback recorded',
        description: type === 'up' ? '🙏 Thank you for your feedback!' : "🙏 We'll take your feedback into account",
      })

      return type
    })
  }

  const handlePrevVideo = () => {
    if (selectedVideoIndex > 0) {
      setSelectedVideoIndex(selectedVideoIndex - 1)
    }
  }

  const handleNextVideo = () => {
    if (selectedVideoIndex < processedVideos.length - 1) {
      setSelectedVideoIndex(selectedVideoIndex + 1)
    }
  }

  const toggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  // Extract evidence data
  const problem = opportunity.PRD?.problem || ''
  const solution = opportunity.PRD?.solution || ''
  const keyElements = opportunity.PRD?.key_elements || []
  const rabbitHoles = opportunity.PRD?.rabbit_holes || []
  const quotes = []
  const needs = []
  const painPoints = []
  const feedbacks = []
  const opportunityEvidence = Array.isArray(opportunity.Evidence) ? opportunity.Evidence : []
  opportunityEvidence.forEach((evidence: any) => {
    if (evidence.note_type === 'quote') {
      const content = evidence.title ? evidence.title + ': ' : '' + evidence.original_transcript_segment
      quotes.push({ id: evidence.note_ID, content })
    } else if (evidence.note_type === 'need') {
      needs.push({ id: evidence.note_ID, content: evidence.note_content })
    } else if (evidence.note_type === 'problem') {
      const content = evidence.title ? evidence.title + ': ' : '' + evidence.note_content
      painPoints.push({ id: evidence.note_ID, content })
    } else if (evidence.note_type === 'feedback') {
      feedbacks.push({ id: evidence.note_ID, content: evidence.note_content })
    }
  })
  let evidenceDistinctMeetings = [...new Set(opportunityEvidence.map((evidence: any) => evidence.meeting_id))]
  evidenceDistinctMeetings = evidenceDistinctMeetings.filter((meeting) => meeting)
  const evidenceDistinctMeetingsCount = evidenceDistinctMeetings.length

  // Count VIP meetings within evidenceDistinctMeetings
  const vipMeetingsCount = evidenceDistinctMeetings.reduce<number>((count, meetingId) => {
    const meeting = meetings.find((m: any) => m.id === meetingId)
    return meeting && meeting.is_vip ? count + 1 : count
  }, 0)

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`p-0 ${isExpanded ? 'w-screen max-w-none h-screen' : 'max-w-none h-screen'}`}
        slideIn={true}
        position="right"
      >
        {/* Only have ONE close button */}
        <Button variant="ghost" size="icon" className="absolute right-4 top-4 z-20" onClick={onClose}>
          <X className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </Button>

        <Button variant="ghost" size="icon" className="absolute right-12 top-4 z-20" onClick={toggleExpand}>
          {isExpanded ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
          <span className="sr-only">{isExpanded ? 'Collapse' : 'Expand'}</span>
        </Button>

        <div className={`h-full flex ${isExpanded ? 'flex-row' : 'flex-col'} overflow-hidden`}>
          {isExpanded ? (
            <>
              <div className="flex-1 flex flex-col h-full items-center">
                <div className="w-full max-w-[940px] flex flex-col h-full">
                  <div className="sticky top-0 z-10 bg-white p-6 pb-2 border-b w-full pl-4">
                    <DialogHeader>
                      <div className="flex items-start justify-between">
                        <DialogTitle className="text-2xl font-manrope font-bold text-[#40424A] pr-10 break-words">
                          {number && (
                            <div className="float-left w-6 h-6 bg-[#F5F6F7] rounded-[4px] flex items-center justify-center shrink-0 mr-2 mt-1">
                              <span className="text-base font-semibold text-[#62718D]">{number}</span>
                            </div>
                          )}
                          {opportunity.title}
                        </DialogTitle>
                      </div>
                    </DialogHeader>
                  </div>

                  <div className="flex-1 overflow-y-auto scrollbar-hide p-6 pt-4 pl-4 pr-[clamp(24px,5vw,100px)]">
                    <div className="mt-2 pb-[100px]">
                      <div className="prose prose-gray max-w-none">
                        <p className="text-[22px] font-medium text-[#62718D] font-['DM_Sans'] whitespace-pre-line">
                          {opportunity.subtitle}
                        </p>

                        {/* Moved action buttons below the tagline */}
                        <div className="flex mt-6 mb-8">
                          <div className="flex-1 flex items-center space-x-4">
                            {onSendToDevTool && currentDevTool && (
                              <Button
                                className="font-manrope flex items-center justify-center gap-2 px-4 py-1.5 text-sm font-medium text-[#40424A] hover:bg-white hover:shadow-[0_4px_22px_rgba(15,21,55,0.05)] hover:border-[#40424A] rounded-lg border border-gray-200 transition-all duration-200"
                                variant="outline"
                                onClick={(e) => onSendToDevTool(e)}
                              >
                                <img
                                  src={getDevToolIcon(currentDevTool as DevTool)}
                                  alt={currentDevTool}
                                  className="h-3.5 w-auto"
                                />
                                Send to {currentDevTool.charAt(0).toUpperCase() + currentDevTool.slice(1)}
                              </Button>
                            )}

                            {toggleArchiveOpportunity && (
                              <Button
                                variant="archive"
                                className="font-manrope flex items-center justify-center px-4 py-1.5 text-sm font-medium text-gray-500 hover:bg-white hover:shadow-[0_4px_22px_rgba(15,21,55,0.05)] hover:border-gray-400 rounded-lg border border-gray-200 transition-all duration-200"
                                onClick={() => toggleArchiveOpportunity(opportunity.id)}
                              >
                                {opportunity.isArchived ? 'Restore' : 'Archive'}
                              </Button>
                            )}
                          </div>

                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleThumbClick('up')}
                              className={cn(
                                'p-1 text-gray-500 hover:text-gray-700 transition-transform',
                                isAnimating && thumbsState === 'up' && 'animate-bounce'
                              )}
                            >
                              <ThumbsUp
                                className="w-5 h-5"
                                fill={thumbsState === 'up' ? '#62718D' : 'none'}
                                color={thumbsState === 'up' ? '#62718D' : 'currentColor'}
                              />
                            </button>
                            <button
                              onClick={() => handleThumbClick('down')}
                              className={cn(
                                'p-1 text-gray-500 hover:text-gray-700 transition-transform',
                                isAnimating && thumbsState === 'down' && 'animate-bounce-down'
                              )}
                            >
                              <ThumbsDown
                                className="w-5 h-5"
                                fill={thumbsState === 'down' ? '#62718D' : 'none'}
                                color={thumbsState === 'down' ? '#62718D' : 'currentColor'}
                              />
                            </button>
                          </div>
                        </div>
                      </div>

                      {processedVideos.length > 0 && (
                        <VideoCarousel
                          videos={processedVideos}
                          handlePrevVideo={handlePrevVideo}
                          handleNextVideo={handleNextVideo}
                          selectedVideoIndex={selectedVideoIndex}
                          setSelectedVideoIndex={setSelectedVideoIndex}
                          isLoading={isLoadingUrls}
                        />
                      )}

                      <div className="prose prose-gray max-w-none mb-8">
                        <h3 className="font-manrope text-lg font-semibold text-[#40424A] mb-4">Problem</h3>
                        <p className="font-manrope text-gray-600 whitespace-pre-line">{problem}</p>
                      </div>

                      <div className="prose prose-gray max-w-none mb-8">
                        <h3 className="font-manrope text-lg font-semibold text-[#40424A] mb-4">Solution</h3>
                        <p className="font-manrope text-gray-600 whitespace-pre-line">{solution}</p>
                      </div>

                      {keyElements.length > 0 && (
                        <div className="prose prose-gray max-w-none mb-8">
                          <h3 className="font-manrope text-lg font-semibold text-[#40424A] mb-4">Key Elements</h3>
                          <ul className="space-y-2">
                            {keyElements.map((element, index) => (
                              <li key={index} className="font-manrope text-gray-600 flex items-start">
                                <span className="mr-2">•</span>
                                {element}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {rabbitHoles.length > 0 && (
                        <div className="prose prose-gray max-w-none mb-8">
                          <h3 className="font-manrope text-lg font-semibold text-[#40424A] mb-4">Rabbit Holes</h3>
                          <ul className="space-y-2">
                            {rabbitHoles.map((rabbitHole, index) => (
                              <li key={index} className="font-manrope text-gray-600 flex items-start">
                                <span className="mr-2">•</span>
                                {rabbitHole.title}: {rabbitHole.description}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {opportunityEvidence.length > 0 && (
                <div className="w-[320px] flex flex-col bg-gray-50">
                  <div className="sticky top-0 z-10 bg-gray-50 p-6 pb-4 border-b border-gray-200">
                    <h2 className="font-manrope text-xl font-bold text-[#40424A]">Supporting Evidence</h2>
                  </div>

                  <div className="flex-1 overflow-y-auto scrollbar-hide p-6 pt-4">
                    <div className="mb-8">
                      <h3 className="font-manrope text-sm font-semibold text-[#40424A] mb-3">In Numbers</h3>
                      <div className="space-y-2">
                        {vipMeetingsCount > 0 && (
                          <div className="flex items-center gap-1.5">
                            <Star className="w-3.5 h-3.5 text-[#FF4A8E]" fill="currentColor" />
                            <span className="font-manrope text-xs font-medium text-[#FF4A8E]">
                              {vipMeetingsCount} VIP
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5 text-[#FF4A8E]" />
                          <span className="font-manrope text-xs font-medium text-[#FF4A8E]">
                            {evidenceDistinctMeetingsCount} sessions
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <CirclePercent className="w-3.5 h-3.5 text-[#40424A]" />
                          <span className="font-manrope text-xs font-medium text-[#40424A]">
                            {Math.round((evidenceDistinctMeetingsCount / meetings.length) * 100)}% of total
                          </span>
                        </div>
                      </div>
                    </div>

                    {quotes.length > 0 && (
                      <div className="mb-8">
                        <h3 className="font-manrope text-sm font-semibold text-[#40424A] mb-3">Quotes</h3>
                        <ul className="space-y-0 divide-y divide-gray-200">
                          {quotes.map((quote, index) => (
                            <li
                              key={index}
                              className="font-manrope text-sm text-gray-600 hover:bg-white transition-colors rounded-md"
                            >
                              <Tippy
                                theme="custom-dark"
                                interactive={true}
                                placement="top"
                                maxWidth={350}
                                content={
                                  <QuoteTooltip
                                    id={quote.id}
                                    content={evidence[quote.id].content}
                                    participantId={evidence[quote.id].participant_id}
                                    whoSaidIt={evidence[quote.id].who_said_it}
                                    startTime={evidence[quote.id].start_time}
                                    originalTranscriptSegment={evidence[quote.id].original_transcript_segment}
                                  />
                                }
                              >
                                <div className="flex items-start gap-2 py-2 px-2">
                                  <Quote className="w-4 h-4 text-[#34AED7] mt-0.5 shrink-0" fill="currentColor" />
                                  {quote.content}
                                </div>
                              </Tippy>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {needs.length > 0 && (
                      <div className="mb-8">
                        <h3 className="font-manrope text-sm font-semibold text-[#40424A] mb-3">Needs</h3>
                        <ul className="space-y-0 divide-y divide-gray-200">
                          {needs.map((need, index) => (
                            <Tippy
                              key={need.id ?? index}
                              theme="custom-dark"
                              interactive={true}
                              placement="top"
                              maxWidth={350}
                              content={
                                <NeedTooltip
                                  id={need.id}
                                  content={evidence[need.id].content}
                                  participantId={evidence[need.id].participant_id}
                                  whoSaidIt={evidence[need.id].who_said_it}
                                  startTime={evidence[need.id].start_time}
                                  referent={evidence[need.id].referent}
                                />
                              }
                            >
                              <li
                                key={index}
                                className="font-manrope text-sm text-gray-600 hover:bg-white transition-colors rounded-md"
                              >
                                <div className="flex items-start gap-2 py-2 px-2">
                                  <StickyNote className="w-4 h-4 text-[#FFC921] mt-0.5 shrink-0" />
                                  {need.content}
                                </div>
                              </li>
                            </Tippy>
                          ))}
                        </ul>
                      </div>
                    )}

                    {painPoints.length > 0 && (
                      <div className="mb-8">
                        <h3 className="font-manrope text-sm font-semibold text-[#40424A] mb-3">Pain Points</h3>
                        <ul className="space-y-0 divide-y divide-gray-200">
                          {painPoints.map((point, index) => (
                            <Tippy
                              key={point.id ?? index}
                              theme="custom-dark"
                              interactive={true}
                              placement="top"
                              maxWidth={350}
                              content={
                                <ProblemTooltip
                                  id={point.id}
                                  content={evidence[point.id].content}
                                  participantId={evidence[point.id].participant_id}
                                  whoSaidIt={evidence[point.id].who_said_it}
                                  startTime={evidence[point.id].start_time}
                                  title={evidence[point.id].title}
                                />
                              }
                            >
                              <li
                                key={index}
                                className="font-manrope text-sm text-gray-600 hover:bg-white transition-colors rounded-md"
                              >
                                <div className="flex items-start gap-2 py-2 px-2">
                                  <Zap className="w-4 h-4 text-[#FF4A8E] mt-0.5 shrink-0" fill="currentColor" />
                                  {point.content}
                                </div>
                              </li>
                            </Tippy>
                          ))}
                        </ul>
                      </div>
                    )}

                    {feedbacks.length > 0 && (
                      <div>
                        <h3 className="font-manrope text-sm font-semibold text-[#40424A] mb-3">Feedback</h3>
                        <ul className="space-y-0 divide-y divide-gray-200">
                          {feedbacks.map((feedback, index) => {
                            let color = '#FFF53C' // neutral by default
                            if (
                              feedback.content.toLowerCase().includes('need') ||
                              feedback.content.toLowerCase().includes('want')
                            ) {
                              color = '#D40814' // negative
                            } else if (
                              feedback.content.toLowerCase().includes('better') ||
                              feedback.content.toLowerCase().includes('more')
                            ) {
                              color = '#1FB912' // positive
                            }

                            return (
                              <Tippy
                                key={feedback.id ?? index}
                                theme="custom-dark"
                                interactive={true}
                                placement="top"
                                maxWidth={350}
                                content={
                                  <FeedbackTooltip
                                    id={feedback.id}
                                    content={evidence[feedback.id].content}
                                    participantId={evidence[feedback.id].participant_id}
                                    whoSaidIt={evidence[feedback.id].who_said_it}
                                    startTime={evidence[feedback.id].start_time}
                                    sentimentAnalysis={evidence[feedback.id].sentiment_analysis}
                                  />
                                }
                              >
                                <li
                                  key={index}
                                  className="font-manrope text-sm text-gray-600 hover:bg-white transition-colors rounded-md"
                                >
                                  <div className="flex items-start gap-2 py-2 px-2">
                                    <Megaphone
                                      className={`w-4 h-4 mt-0.5 shrink-0`}
                                      style={{ color }}
                                      fill="currentColor"
                                    />
                                    {feedback.content}
                                  </div>
                                </li>
                              </Tippy>
                            )
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-full overflow-y-auto scrollbar-hide">
              <div className="flex flex-col">
                <div className="sticky top-0 z-10 bg-white p-6 pb-2 border-b pl-4">
                  <div className="flex items-start justify-between">
                    <DialogTitle className="text-2xl font-manrope font-bold text-[#40424A] pr-10 break-words">
                      {number && (
                        <div className="float-left w-6 h-6 bg-[#F5F6F7] rounded-[4px] flex items-center justify-center shrink-0 mr-2 mt-1">
                          <span className="text-base font-semibold text-[#62718D]">{number}</span>
                        </div>
                      )}
                      {opportunity.title}
                    </DialogTitle>
                  </div>
                </div>

                <div className="p-6 pt-4 pl-4 pr-[clamp(24px,5vw,100px)]">
                  <div className="mt-2 pb-[100px]">
                    <div className="prose prose-gray max-w-none">
                      <p className="text-[22px] font-medium text-[#62718D] font-['DM_Sans'] whitespace-pre-line">
                        {opportunity.subtitle}
                      </p>

                      {/* Buttons in side-slider state below the tagline */}
                      <div className="flex mt-6 mb-8">
                        <div className="flex-1 flex items-center space-x-4">
                          {onSendToDevTool && currentDevTool && (
                            <Button
                              className="font-manrope flex items-center justify-center gap-2 px-4 py-1.5 text-sm font-medium text-[#40424A] hover:bg-white hover:shadow-[0_4px_22px_rgba(15,21,55,0.05)] hover:border-[#40424A] rounded-lg border border-gray-200 transition-all duration-200"
                              variant="outline"
                              onClick={(e) => onSendToDevTool(e)}
                            >
                              <img
                                src={getDevToolIcon(currentDevTool as DevTool)}
                                alt={currentDevTool}
                                className="h-3.5 w-auto"
                              />
                              Send to {currentDevTool.charAt(0).toUpperCase() + currentDevTool.slice(1)}
                            </Button>
                          )}

                          {toggleArchiveOpportunity && (
                            <Button
                              variant="archive"
                              className="font-manrope flex items-center justify-center px-4 py-1.5 text-sm font-medium text-gray-500 hover:bg-white hover:shadow-[0_4px_22px_rgba(15,21,55,0.05)] hover:border-gray-400 rounded-lg border border-gray-200 transition-all duration-200"
                              onClick={() => toggleArchiveOpportunity(opportunity.id)}
                            >
                              {opportunity.isArchived ? 'Restore' : 'Archive'}
                            </Button>
                          )}
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleThumbClick('up')}
                            className={cn(
                              'p-1 text-gray-500 hover:text-gray-700 transition-transform',
                              isAnimating && thumbsState === 'up' && 'animate-bounce'
                            )}
                          >
                            <ThumbsUp
                              className="w-5 h-5"
                              fill={thumbsState === 'up' ? '#62718D' : 'none'}
                              color={thumbsState === 'up' ? '#62718D' : 'currentColor'}
                            />
                          </button>
                          <button
                            onClick={() => handleThumbClick('down')}
                            className={cn(
                              'p-1 text-gray-500 hover:text-gray-700 transition-transform',
                              isAnimating && thumbsState === 'down' && 'animate-bounce-down'
                            )}
                          >
                            <ThumbsDown
                              className="w-5 h-5"
                              fill={thumbsState === 'down' ? '#62718D' : 'none'}
                              color={thumbsState === 'down' ? '#62718D' : 'currentColor'}
                            />
                          </button>
                        </div>
                      </div>
                    </div>

                    {processedVideos.length > 0 && (
                      <VideoCarouselExpanded
                        videos={processedVideos}
                        handlePrevVideo={handlePrevVideo}
                        handleNextVideo={handleNextVideo}
                        selectedVideoIndex={selectedVideoIndex}
                        setSelectedVideoIndex={setSelectedVideoIndex}
                        isLoading={isLoadingUrls}
                      />
                    )}

                    {problem && (
                      <div className="prose prose-gray max-w-none mb-8">
                        <h3 className="font-manrope text-lg font-semibold text-[#40424A] mb-4">Problem</h3>
                        <p className="font-manrope text-gray-600 whitespace-pre-line">{problem}</p>
                      </div>
                    )}

                    {solution && (
                      <div className="prose prose-gray max-w-none mb-8">
                        <h3 className="font-manrope text-lg font-semibold text-[#40424A] mb-4">Solution</h3>
                        <p className="font-manrope text-gray-600 whitespace-pre-line">{solution}</p>
                      </div>
                    )}

                    {keyElements.length > 0 && (
                      <div className="prose prose-gray max-w-none mb-8">
                        <h3 className="font-manrope text-lg font-semibold text-[#40424A] mb-4">Key Elements</h3>
                        <ul className="space-y-2">
                          {keyElements.map((element, index) => (
                            <li key={index} className="font-manrope text-gray-600 flex items-start">
                              <span className="mr-2">•</span>
                              {element}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {rabbitHoles.length > 0 && (
                      <div className="prose prose-gray max-w-none mb-8">
                        <h3 className="font-manrope text-lg font-semibold text-[#40424A] mb-4">Rabbit Holes</h3>
                        <ul className="space-y-2">
                          {rabbitHoles.map((rabbitHole, index) => (
                            <li key={index} className="font-manrope text-gray-600 flex items-start">
                              <span className="mr-2">•</span>
                              {rabbitHole.title}: {rabbitHole.description}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border-t border-gray-200">
                <div className="p-6 pb-4">
                  <h2 className="font-manrope text-xl font-bold text-[#40424A]">Supporting Evidence</h2>
                </div>

                <div className="p-6 pt-4">
                  <div className="mb-8">
                    <h3 className="font-manrope text-sm font-semibold text-[#40424A] mb-3">In Numbers</h3>
                    <div className="space-y-2">
                      {vipMeetingsCount > 0 && (
                        <div className="flex items-center gap-1.5">
                          <Star className="w-3.5 h-3.5 text-[#FF4A8E]" fill="currentColor" />
                          <span className="font-manrope text-xs font-medium text-[#FF4A8E]">
                            {vipMeetingsCount} VIP
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-[#FF4A8E]" />
                        <span className="font-manrope text-xs font-medium text-[#FF4A8E]">
                          {evidenceDistinctMeetingsCount} sessions
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <CirclePercent className="w-3.5 h-3.5 text-[#40424A]" />
                        <span className="font-manrope text-xs font-medium text-[#40424A]">
                          {Math.round((evidenceDistinctMeetingsCount / meetings.length) * 100)}% of total
                        </span>
                      </div>
                    </div>
                  </div>

                  {quotes.length > 0 && (
                    <div className="mb-8">
                      <h3 className="font-manrope text-sm font-semibold text-[#40424A] mb-3">Quotes</h3>
                      <ul className="space-y-0 divide-y divide-gray-200">
                        {quotes.map((quote, index) => (
                          <li
                            key={index}
                            className="font-manrope text-sm text-gray-600 hover:bg-white transition-colors rounded-md"
                          >
                            <Tippy
                              theme="custom-dark"
                              interactive={true}
                              placement="top"
                              maxWidth={350}
                              content={
                                <QuoteTooltip
                                  id={quote.id}
                                  content={evidence[quote.id].content}
                                  participantId={evidence[quote.id].participant_id}
                                  whoSaidIt={evidence[quote.id].who_said_it}
                                  startTime={evidence[quote.id].start_time}
                                  originalTranscriptSegment={evidence[quote.id].original_transcript_segment}
                                />
                              }
                            >
                              <div className="flex items-start gap-2 py-2 px-2">
                                <Quote className="w-4 h-4 text-[#34AED7] mt-0.5 shrink-0" fill="currentColor" />
                                {quote.content}
                              </div>
                            </Tippy>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {needs.length > 0 && (
                    <div className="mb-8">
                      <h3 className="font-manrope text-sm font-semibold text-[#40424A] mb-3">Needs</h3>
                      <ul className="space-y-0 divide-y divide-gray-200">
                        {needs.map((need, index) => (
                          <li
                            key={index}
                            className="font-manrope text-sm text-gray-600 hover:bg-white transition-colors rounded-md"
                          >
                            <Tippy
                              theme="custom-dark"
                              interactive={true}
                              placement="top"
                              maxWidth={350}
                              content={
                                <NeedTooltip
                                  id={need.id}
                                  content={evidence[need.id].content}
                                  participantId={evidence[need.id].participant_id}
                                  whoSaidIt={evidence[need.id].who_said_it}
                                  startTime={evidence[need.id].start_time}
                                  referent={evidence[need.id].referent}
                                />
                              }
                            >
                              <div className="flex items-start gap-2 py-2 px-2">
                                <StickyNote className="w-4 h-4 text-[#FFC921] mt-0.5 shrink-0" />
                                {need.content}
                              </div>
                            </Tippy>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {painPoints.length > 0 && (
                    <div className="mb-8">
                      <h3 className="font-manrope text-sm font-semibold text-[#40424A] mb-3">Pain Points</h3>
                      <ul className="space-y-0 divide-y divide-gray-200">
                        {painPoints.map((point, index) => (
                          <li
                            key={index}
                            className="font-manrope text-sm text-gray-600 hover:bg-white transition-colors rounded-md"
                          >
                            <Tippy
                              theme="custom-dark"
                              interactive={true}
                              placement="top"
                              maxWidth={350}
                              content={
                                <ProblemTooltip
                                  id={point.id}
                                  content={evidence[point.id].content}
                                  participantId={evidence[point.id].participant_id}
                                  whoSaidIt={evidence[point.id].who_said_it}
                                  startTime={evidence[point.id].start_time}
                                  title={evidence[point.id].title}
                                />
                              }
                            >
                              <div className="flex items-start gap-2 py-2 px-2">
                                <Zap className="w-4 h-4 text-[#FF4A8E] mt-0.5 shrink-0" fill="currentColor" />
                                {point.content}
                              </div>
                            </Tippy>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {feedbacks.length > 0 && (
                    <div>
                      <h3 className="font-manrope text-sm font-semibold text-[#40424A] mb-3">Feedback</h3>
                      <ul className="space-y-0 divide-y divide-gray-200">
                        {feedbacks.map((feedback, index) => {
                          let color = '#FFF53C' // neutral by default
                          if (
                            feedback.content.toLowerCase().includes('need') ||
                            feedback.content.toLowerCase().includes('want')
                          ) {
                            color = '#D40814' // negative
                          } else if (
                            feedback.content.toLowerCase().includes('better') ||
                            feedback.content.toLowerCase().includes('more')
                          ) {
                            color = '#1FB912' // positive
                          }

                          return (
                            <li
                              key={index}
                              className="font-manrope text-sm text-gray-600 hover:bg-white transition-colors rounded-md"
                            >
                              <Tippy
                                theme="custom-dark"
                                interactive={true}
                                placement="top"
                                maxWidth={350}
                                content={
                                  <FeedbackTooltip
                                    id={feedback.id}
                                    content={evidence[feedback.id].content}
                                    participantId={evidence[feedback.id].participant_id}
                                    whoSaidIt={evidence[feedback.id].who_said_it}
                                    startTime={evidence[feedback.id].start_time}
                                    sentimentAnalysis={evidence[feedback.id].sentiment_analysis}
                                  />
                                }
                              >
                                <div className="flex items-start gap-2 py-2 px-2">
                                  <Megaphone
                                    className={`w-4 h-4 mt-0.5 shrink-0`}
                                    style={{ color }}
                                    fill="currentColor"
                                  />
                                  {feedback.content}
                                </div>
                              </Tippy>
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default OpportunityDialog
